import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutInfo, testimonials } from '@/mocks/comics';

export default function AboutPage() {
  const [testimonialIdx, setTestimonialIdx] = useState(0);
  const t = testimonials[testimonialIdx % testimonials.length];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '36漫画网',
    url: 'http://jhequip.com/',
    description: aboutInfo.introduction,
    foundingDate: '2020',
    slogan: aboutInfo.tagline,
    contactPoint: {
      '@type': 'ContactPoint',
      email: aboutInfo.contactEmail,
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
    sameAs: ['http://jhequip.com/'],
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于36漫画网 - 品牌介绍与联系方式',
    description: aboutInfo.introduction,
    url: 'http://jhequip.com/about',
    inLanguage: 'zh-CN',
    about: {
      '@type': 'Organization',
      name: '36漫画网',
    },
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>

      <Navbar />

      <main className="pt-16">
        <section className="bg-background-50">
          <div className="mx-auto px-4 md:px-6 pt-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="mx-auto px-4 md:px-6 max-w-3xl text-center">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 rounded-2xl bg-background-100">
              <i className="ri-book-read-line text-3xl text-primary-600"></i>
            </div>
            <p className="text-foreground-400 text-sm mb-4">关于我们</p>
            <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-6 leading-snug">
              {aboutInfo.tagline}
            </h1>
            <div className="max-w-2xl mx-auto">
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-4">
                {aboutInfo.introduction}
              </p>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                {aboutInfo.mission}
              </p>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100/70">
          <div className="mx-auto px-4 md:px-6 max-w-3xl">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-3">
                平台
                <span className="font-serif italic font-normal text-foreground-400">口碑</span>
              </h2>
            </div>

            <div className="relative bg-background-50 rounded-2xl p-6 md:p-10">
              <div className="flex items-center gap-2 mb-6">
                <div className="flex gap-0.5">
                  {[...Array(t.rating)].map((_, i) => (
                    <div key={i} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-400 text-sm"></i>
                    </div>
                  ))}
                </div>
                <span className="px-2.5 py-0.5 bg-background-900 text-white text-xs font-medium rounded-full whitespace-nowrap">
                  {t.rating}.0
                </span>
              </div>
              <blockquote className="text-foreground-700 text-base md:text-lg leading-relaxed mb-8 font-serif italic">
                &ldquo;{t.text}&rdquo;
              </blockquote>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                  <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                </div>
                <div>
                  <div className="text-foreground-900 font-medium text-sm">{t.name}</div>
                  <div className="text-foreground-400 text-xs">{t.role}</div>
                </div>
              </div>
              <div className="absolute bottom-6 right-6 md:bottom-10 md:right-10 flex gap-2">
                <button
                  onClick={() => setTestimonialIdx((p) => (p === 0 ? testimonials.length - 1 : p - 1))}
                  className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-300 bg-background-50 hover:bg-background-100 text-foreground-600 transition-colors cursor-pointer"
                  aria-label="上一条评价"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                <button
                  onClick={() => setTestimonialIdx((p) => (p === testimonials.length - 1 ? 0 : p + 1))}
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-900 hover:bg-background-800 text-white transition-colors cursor-pointer"
                  aria-label="下一条评价"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 border border-background-300 rounded-full text-foreground-600 text-xs font-medium mb-4 whitespace-nowrap">
                核心优势
              </span>
              <h2 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-3">
                为什么选择
                <span className="block">36漫画网？</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
              {aboutInfo.advantages.map((adv, idx) => (
                <div key={idx} className="group p-6 rounded-2xl bg-background-100 hover:bg-primary-50 transition-colors duration-300 cursor-default">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-background-200 mb-4 group-hover:bg-primary-100 transition-colors">
                    <i className={`${adv.icon} text-xl text-foreground-700 group-hover:text-primary-600 transition-colors`}></i>
                  </div>
                  <h3 className="text-foreground-900 font-semibold text-sm mb-2">{adv.title}</h3>
                  <p className="text-foreground-500 text-xs leading-relaxed">{adv.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="mx-auto px-4 md:px-6">
            <div className="relative rounded-3xl overflow-hidden">
              <div className="aspect-[21/9] md:aspect-[3/1]">
                <img
                  src="https://readdy.ai/api/search-image?query=Stylized%20Korean%20manhwa%20community%20atmosphere%2C%20warm%20amber%20and%20soft%20cream%20gradient%2C%20abstract%20reading%20and%20connection%20aesthetic%2C%20cozy%20creative%20workspace%20mood%2C%20minimal%20digital%20art%20with%20film%20grain%20texture%2C%20no%20text%2C%20atmospheric%20lighting&width=1600&height=600&seq=cta-about-contact&orientation=landscape"
                  alt="联系我们 - 36漫画网"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/20 flex items-center">
                <div className="px-6 md:px-12 py-10 max-w-xl">
                  <h2 className="text-2xl md:text-4xl font-bold font-heading text-white mb-3 leading-tight">
                    与我们
                    <span className="block text-accent-400 italic font-normal">取得联系</span>
                  </h2>
                  <p className="text-white/70 text-sm md:text-base mb-6 leading-relaxed">
                    有任何问题或建议？我们期待听到您的声音
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-white/80 text-sm">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-mail-line"></i>
                      </div>
                      <span>{aboutInfo.contactEmail}</span>
                    </div>
                    <div className="flex items-center gap-3 text-white/80 text-sm">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-wechat-line"></i>
                      </div>
                      <span>微信：{aboutInfo.contactWechat}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}